import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { PHONE_REGEX } from '@paymash/sdk/regex';

import { MatchValidator, RequiredValidator } from '@modules/core/form-validators';
import { ModelFormGroup } from '@modules/core/types';
import {
  AuthorizedForm,
  ContactDetailsForm,
  ExternalNoteForm,
  FeedbackForm,
  ForgotPasswordForm,
  LoginForm,
  RegistrationForm,
  ResetPasswordForm,
} from '@modules/core/interfaces';

@Injectable({
  providedIn: 'root',
})
export class FormService {
  constructor(private fb: FormBuilder) {}

  public initContactDetailsForm(): FormGroup<ModelFormGroup<ContactDetailsForm>> {
    return this.fb.group<ModelFormGroup<ContactDetailsForm>>({
      salutation: this.fb.control(null),
      firstName: this.fb.nonNullable.control('', [RequiredValidator]),
      lastName: this.fb.nonNullable.control('', [RequiredValidator]),
      email: this.fb.nonNullable.control('', [RequiredValidator, Validators.email]),
      phone: this.fb.nonNullable.control('', [RequiredValidator, Validators.pattern(PHONE_REGEX)]),
      comment: this.fb.nonNullable.control(''),
    });
  }

  public initRegistrationForm(): FormGroup<ModelFormGroup<RegistrationForm>> {
    return this.fb.group<ModelFormGroup<RegistrationForm>>(
      {
        salutation: this.fb.control(null),
        firstName: this.fb.nonNullable.control('', [RequiredValidator]),
        lastName: this.fb.nonNullable.control('', [RequiredValidator]),
        email: this.fb.nonNullable.control('', [RequiredValidator, Validators.email]),
        phone: this.fb.nonNullable.control('', [RequiredValidator, Validators.pattern(PHONE_REGEX)]),
        password: this.fb.nonNullable.control('', [RequiredValidator]),
        passwordConfirm: this.fb.nonNullable.control('', [RequiredValidator]),
      },
      {
        validators: MatchValidator('password', 'passwordConfirm'),
      }
    );
  }

  public initAuthorizedForm(): FormGroup<ModelFormGroup<AuthorizedForm>> {
    return this.fb.group<ModelFormGroup<AuthorizedForm>>({
      editMode: this.fb.nonNullable.control(false),
      salutation: this.fb.nonNullable.control(''),
      firstName: this.fb.nonNullable.control('', [RequiredValidator]),
      lastName: this.fb.nonNullable.control('', [RequiredValidator]),
      email: this.fb.nonNullable.control('', [RequiredValidator, Validators.email]),
      phone: this.fb.nonNullable.control('', [RequiredValidator, Validators.pattern(PHONE_REGEX)]),
      comment: this.fb.nonNullable.control(''),
    });
  }

  public initLoginForm(): FormGroup<ModelFormGroup<LoginForm>> {
    return this.fb.group<ModelFormGroup<LoginForm>>({
      email: this.fb.nonNullable.control('', [RequiredValidator, Validators.email]),
      password: this.fb.nonNullable.control('', [RequiredValidator]),
    });
  }

  public initForgotPasswordForm(): FormGroup<ModelFormGroup<ForgotPasswordForm>> {
    return this.fb.group<ModelFormGroup<ForgotPasswordForm>>({
      email: this.fb.nonNullable.control('', [RequiredValidator, Validators.email]),
    });
  }

  public initResetPasswordForm(): FormGroup<ModelFormGroup<ResetPasswordForm>> {
    return this.fb.group<ModelFormGroup<ResetPasswordForm>>(
      {
        password: this.fb.nonNullable.control('', [RequiredValidator]),
        passwordConfirm: this.fb.nonNullable.control('', [RequiredValidator]),
      },
      {
        validators: MatchValidator('password', 'passwordConfirm'),
      }
    );
  }

  initFeedbackForm(): FormGroup<ModelFormGroup<FeedbackForm>> {
    return this.fb.group<ModelFormGroup<FeedbackForm>>({
      state: this.fb.nonNullable.control('', [RequiredValidator]),
      text: this.fb.nonNullable.control('', [RequiredValidator]),
    });
  }

  initExternalNoteForm(): FormGroup<ModelFormGroup<ExternalNoteForm>> {
    return this.fb.group<ModelFormGroup<ExternalNoteForm>>({
      externalNote: this.fb.nonNullable.control('', [RequiredValidator]),
    });
  }
}
